import {
  type QuestEvent,
  type QuestPeriod,
  type Reward,
} from './config';
import { QUEST_OBJECTIVES, questObjective, type QuestObjectiveDef } from './quest-objectives';


export interface QuestRewardOption {
  id: string;
  period: QuestPeriod;
  weight: number;
  effort: number;
  reward: Reward;
}


export const QUEST_REWARD_OPTIONS: readonly QuestRewardOption[] = [
  { id: 'standard-3', period: 'daily', weight: 16, effort: 8, reward: { boosters: { standard: 3 } } },
  { id: 'standard-5', period: 'daily', weight: 8, effort: 14, reward: { boosters: { standard: 5 } } },
  { id: 'premium-1', period: 'daily', weight: 18, effort: 12, reward: { boosters: { premium: 1 } } },
  { id: 'premium-2', period: 'daily', weight: 8, effort: 22, reward: { boosters: { premium: 2 } } },
  { id: 'fragments-40', period: 'daily', weight: 14, effort: 10, reward: { fragments: 40, fragmentsChoice: true } },
  { id: 'fragments-75', period: 'daily', weight: 8, effort: 18, reward: { fragments: 75, fragmentsChoice: true } },
  { id: 'uncommon', period: 'daily', weight: 10, effort: 7, reward: { characterRarity: 'uncommon' } },
  { id: 'rare-daily', period: 'daily', weight: 7, effort: 16, reward: { characterRarity: 'rare' } },
  { id: 'xp-100', period: 'daily', weight: 12, effort: 12, reward: { xp: 100 } },
  { id: 'anima-200', period: 'daily', weight: 12, effort: 10, reward: { anima: 200 } },

  { id: 'premium-3', period: 'weekly', weight: 14, effort: 40, reward: { boosters: { premium: 3 } } },
  { id: 'premium-6', period: 'weekly', weight: 6, effort: 80, reward: { boosters: { premium: 6 } } },
  { id: 'premium-4', period: 'weekly', weight: 8, effort: 55, reward: { boosters: { premium: 4 } } },
  { id: 'chanceux-1', period: 'weekly', weight: 12, effort: 60, reward: { boosters: { legendary: 1 } } },
  { id: 'chanceux-2', period: 'weekly', weight: 6, effort: 120, reward: { boosters: { legendary: 2 } } },
  { id: 'chanceux-3', period: 'weekly', weight: 3, effort: 180, reward: { boosters: { legendary: 3 } } },
  { id: 'fragments-250', period: 'weekly', weight: 10, effort: 55, reward: { fragments: 250, fragmentsChoice: true } },
  { id: 'fragments-400', period: 'weekly', weight: 6, effort: 90, reward: { fragments: 400, fragmentsChoice: true } },
  { id: 'fragments-650', period: 'weekly', weight: 3, effort: 140, reward: { fragments: 650, fragmentsChoice: true } },
  { id: 'rare-weekly', period: 'weekly', weight: 8, effort: 30, reward: { characterRarity: 'rare' } },
  { id: 'epic', period: 'weekly', weight: 10, effort: 80, reward: { characterRarity: 'epic' } },
  { id: 'legendary-card', period: 'weekly', weight: 5, effort: 160, reward: { characterRarity: 'legendary' } },
  { id: 'mythic', period: 'weekly', weight: 3, effort: 260, reward: { characterRarity: 'mythic' } },
  { id: 'divine', period: 'weekly', weight: 2, effort: 420, reward: { characterRarity: 'divine' } },
  { id: 'standard-8', period: 'weekly', weight: 8, effort: 45, reward: { boosters: { standard: 8 } } },
  { id: 'anima-600', period: 'weekly', weight: 10, effort: 50, reward: { anima: 600 } },

  { id: 'premium-12', period: 'monthly', weight: 12, effort: 200, reward: { boosters: { premium: 12 } } },
  { id: 'chanceux-6', period: 'monthly', weight: 14, effort: 260, reward: { boosters: { legendary: 6 } } },
  { id: 'chanceux-10', period: 'monthly', weight: 6, effort: 380, reward: { boosters: { legendary: 10 } } },
  { id: 'chanceux-12', period: 'monthly', weight: 4, effort: 500, reward: { boosters: { legendary: 12 } } },
  { id: 'epic-monthly', period: 'monthly', weight: 10, effort: 180, reward: { characterRarity: 'epic', anima: 500 } },
  { id: 'legendary-card-monthly', period: 'monthly', weight: 12, effort: 320, reward: { characterRarity: 'legendary' } },
  { id: 'mythic-monthly', period: 'monthly', weight: 8, effort: 520, reward: { characterRarity: 'mythic', anima: 1000 } },
  { id: 'divine-monthly', period: 'monthly', weight: 4, effort: 850, reward: { characterRarity: 'divine' } },
  { id: 'fragments-1500', period: 'monthly', weight: 12, effort: 260, reward: { fragments: 1500, fragmentsChoice: true } },
  { id: 'fragments-2500', period: 'monthly', weight: 6, effort: 380, reward: { fragments: 2500, fragmentsChoice: true } },
  { id: 'anima-3000', period: 'monthly', weight: 10, effort: 240, reward: { anima: 3000 } },
  { id: 'standard-40', period: 'monthly', weight: 8, effort: 180, reward: { boosters: { standard: 40 } } },
];

const DAILY_SLOTS = 4;
const WEEKLY_SLOTS = 4;
const MONTHLY_SLOTS = 3;

export interface RolledQuest {
  id: string;
  period: QuestPeriod;
  event: QuestEvent;
  target: number;
  variant: number;
  effort: number;
  title: string;
  description: string;
  reward: Reward;
}

export interface StoredQuestSpec {
  period: QuestPeriod;
  event: QuestEvent;
  target: number;
  variant?: number;
  effort?: number;
  title: string;
  description: string;
  reward: Reward;
}

function fillCount(template: string, target: number): string {
  return template.replaceAll('{n}', String(target));
}


export function objectiveTarget(event: QuestEvent, effort: number): number | null {
  const objective = questObjective(event);
  if (!objective) return null;
  if (objective.minEffort !== undefined && effort < objective.minEffort) return null;
  if (objective.maxEffort !== undefined && effort > objective.maxEffort) return null;
  if (objective.fixed !== undefined) return objective.fixed;
  return Math.max(1, Math.round(effort * objective.scale));
}

export function questObjectiveCopy(
  event: QuestEvent,
  target: number,
  variant = 0,
): { title: string; description: string } {
  const objective = questObjective(event);
  const copy = objective?.variants[variant] ?? objective?.variants[0];
  if (!copy) return { title: 'Quête', description: 'Termine l’objectif.' };
  const template = target === 1 ? copy.one : copy.other;
  return { title: copy.title, description: fillCount(template, target) };
}

function utcDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}


export function questWeekStart(date: Date): Date {
  const day = utcDay(date);
  const weekday = day.getUTCDay() || 7;
  day.setUTCDate(day.getUTCDate() - weekday + 1);
  return day;
}

export function questPeriodKey(period: QuestPeriod, date: Date): string {
  if (period === 'daily') return date.toISOString().slice(0, 10);
  if (period === 'monthly') return date.toISOString().slice(0, 7);
  const monday = questWeekStart(date);
  const thursday = new Date(monday);
  thursday.setUTCDate(monday.getUTCDate() + 3);
  const year = thursday.getUTCFullYear();
  const yearStart = new Date(Date.UTC(year, 0, 1));
  const week = Math.ceil((thursday.getTime() - yearStart.getTime()) / 86_400_000 / 7 + 1 / 7);
  return `${year}-W${String(week).padStart(2, '0')}`;
}

export function questPeriodEndsAt(period: QuestPeriod, date: Date): Date {
  if (period === 'daily') return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 1));
  if (period === 'monthly') return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1));
  const end = questWeekStart(date);
  end.setUTCDate(end.getUTCDate() + 7);
  return end;
}

function hashSeed(text: string): number {
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function pickWeighted<T extends { weight: number }>(items: readonly T[], random: () => number): T {
  const total = items.reduce((sum, item) => sum + item.weight, 0);
  let cursor = random() * total;
  for (const item of items) {
    cursor -= item.weight;
    if (cursor < 0) return item;
  }
  return items[items.length - 1]!;
}

function fittingObjectives(period: QuestPeriod, effort: number, blocked: ReadonlySet<QuestEvent>): QuestObjectiveDef[] {
  return QUEST_OBJECTIVES.filter(
    (objective) =>
      objective.periods.includes(period) && !blocked.has(objective.event) && objectiveTarget(objective.event, effort) !== null,
  );
}

function rollPeriod(userId: string, period: QuestPeriod, date: Date): RolledQuest[] {
  const slots = period === 'daily' ? DAILY_SLOTS : period === 'weekly' ? WEEKLY_SLOTS : MONTHLY_SLOTS;
  const random = mulberry32(hashSeed(`${userId}|${period}|${questPeriodKey(period, date)}`));
  const pool = QUEST_REWARD_OPTIONS.filter((option) => option.period === period);
  const usedRewards = new Set<string>();
  const usedEvents = new Set<QuestEvent>();
  const quests: RolledQuest[] = [];

  for (let slot = 1; slot <= slots; slot += 1) {
    const available = pool.filter((option) => !usedRewards.has(option.id));
    const reward = pickWeighted(available.length > 0 ? available : pool, random);
    usedRewards.add(reward.id);
    const fresh = fittingObjectives(period, reward.effort, usedEvents);
    const objective = pickWeighted(fresh.length > 0 ? fresh : fittingObjectives(period, reward.effort, new Set()), random);
    usedEvents.add(objective.event);
    const variant = Math.floor(random() * objective.variants.length);
    const target = objectiveTarget(objective.event, reward.effort)!;
    const copy = questObjectiveCopy(objective.event, target, variant);
    quests.push({
      id: period === 'daily' ? `d${slot}` : period === 'weekly' ? `w${slot}` : `m${slot}`,
      period,
      event: objective.event,
      target,
      variant,
      effort: reward.effort,
      title: copy.title,
      description: copy.description,
      reward: reward.reward,
    });
  }
  return quests;
}


export function rollQuestBoard(userId: string, date: Date): RolledQuest[] {
  return [...rollPeriod(userId, 'daily', date), ...rollPeriod(userId, 'weekly', date), ...rollPeriod(userId, 'monthly', date)];
}

export function questSpec(quest: Pick<RolledQuest, 'period' | 'event' | 'target' | 'variant' | 'effort' | 'title' | 'description' | 'reward'>): string {
  const spec: StoredQuestSpec = {
    period: quest.period,
    event: quest.event,
    target: quest.target,
    variant: quest.variant,
    effort: quest.effort,
    title: quest.title,
    description: quest.description,
    reward: quest.reward,
  };
  return JSON.stringify(spec);
}

export function parseQuestSpec(raw: string | null | undefined): StoredQuestSpec | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as StoredQuestSpec;
    if (!value || typeof value.target !== 'number' || !value.event || !value.reward) return null;
    if (value.variant !== undefined && !Number.isInteger(value.variant)) return null;
    return value;
  } catch {
    return null;
  }
}

function effortOf(spec: StoredQuestSpec): number {
  if (typeof spec.effort === 'number' && spec.effort > 0) return spec.effort;
  const match = QUEST_REWARD_OPTIONS.find((option) => JSON.stringify(option.reward) === JSON.stringify(spec.reward));
  if (match) return match.effort;
  return spec.period === 'daily' ? 8 : spec.period === 'weekly' ? 40 : 200;
}


export function rerollQuestSpec(userId: string, periodKey: string, questId: string, spec: StoredQuestSpec, blockedEvents: readonly QuestEvent[]): StoredQuestSpec {
  const effort = effortOf(spec);
  const random = mulberry32(hashSeed(`${userId}|reroll|${spec.period}|${periodKey}|${questId}`));
  const blocked = new Set<QuestEvent>([spec.event, ...blockedEvents]);
  const fresh = fittingObjectives(spec.period, effort, blocked);
  const fallback = fittingObjectives(spec.period, effort, new Set([spec.event]));
  const pool = fresh.length > 0 ? fresh : fallback.length > 0 ? fallback : fittingObjectives(spec.period, effort, new Set());
  const objective = pickWeighted(pool, random);
  const variantCount = objective.variants.length;
  let variant = Math.floor(random() * variantCount);
  if (objective.event === spec.event && variant === (spec.variant ?? 0) && variantCount > 1) {
    variant = (variant + 1) % variantCount;
  }
  const target = objectiveTarget(objective.event, effort) ?? spec.target;
  const copy = questObjectiveCopy(objective.event, target, variant);
  return {
    period: spec.period,
    event: objective.event,
    target,
    variant,
    effort,
    title: copy.title,
    description: copy.description,
    reward: spec.reward,
  };
}
